import React, { useCallback, useEffect, useState } from "react";
import { ArrowRightToLine, Link2, Lock, Unlock } from "lucide-react";
import { toast } from "sonner";
import { api } from "../lib/api";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";

/** One schedule for RA + FRS: link groups (same date, two-way), dependencies (one-way, working-day
 *  offset), and the confirm flow for parents with locked children. Mirrors backend/fieldops/schedule.py */
const COORDS = ["level", "building", "floor", "area", "room_number", "room_type"];
const ORDER = ["project", "building", "floor", "area", "room"];
const RANK = { project: 0, building: 1, floor: 2, area: 3, room: 4 };
export const nodeOf = (n) => ({ ...Object.fromEntries(COORDS.map((k) => [k, String(n[k] ?? "")])), level: n.level || "project", scope_type: String(n.scope_type || "") });
const ctok = (n) => COORDS.map((k) => String(n[k] ?? "")).join("|");
const tok = (n) => `${ctok(n)}|${n.scope_type || ""}`;
const parentOf = (n) => {
  if (n.level === "project") return null;
  const idx = ORDER.indexOf(n.level);
  const p = { ...n, level: ORDER[idx - 1] };
  ["building", "floor", "area", "room_number"].slice(idx - 1).forEach((k) => { p[k] = ""; });
  if (idx - 1 < 4) p.room_type = "";
  return p;
};
const ancestors = (n) => { const out = [n]; let c = parentOf(n); while (c) { out.push(c); c = parentOf(c); } return out; };
const under = (n, k) => {
  if (RANK[n.level] < RANK[k.level]) return false;
  const cov = { project: [], building: ["building"], floor: ["building", "floor"], area: ["building", "floor", "area"], room: ["building", "floor", "area", "room_number", "room_type"] }[k.level];
  return cov.every((c) => String(n[c] || "") === String(k[c] || ""));
};
export const pathLabel = (n) => {
  const p = [];
  if (n.level === "project") p.push(n.scope_type ? "Scope" : "Full Job");
  else {
    if (n.building) p.push(`Bldg ${n.building}`);
    if (["floor", "area", "room"].includes(n.level)) p.push(`Floor ${n.floor || "—"}`);
    if (["area", "room"].includes(n.level)) p.push(n.area ? `Area ${n.area}` : "Unassigned");
    if (n.level === "room") p.push(`Room ${n.room_number}`);
  }
  return p.join(" › ");
};

export const groupAt = (rel, n) => {
  if (!n.scope_type) return null;
  for (const a of ancestors(n)) {
    const at = ctok(a);
    const g = (rel.groups || []).find((x) => ctok(x.node) === at && x.scopes.includes(n.scope_type));
    if (!g) continue;
    const broken = new Set((rel.breaks || []).filter((b) => b.group_id === g.id && under(n, b.node) && RANK[b.node.level] > RANK[a.level]).map((b) => b.scope));
    if (broken.has(n.scope_type)) return null;
    const doneSet = new Set(rel.done || []);
    const all = g.scopes.filter((s) => s !== n.scope_type && !broken.has(s));
    const suspended = all.filter((s) => doneSet.has(tok({ ...n, scope_type: s })));   // partner complete here → link paused at this node
    return { group: g, at: a, own: at === ctok(n), partners: all.filter((s) => !suspended.includes(s)), suspended };
  }
  return null;
};
export const depInto = (rel, n) => (rel.deps || []).find((d) => tok(d.succ) === tok(n)) || null;

export const useSchedule = (projectId) => {
  const [rel, setRel] = useState({ groups: [], breaks: [], deps: [], project_dates: {}, day_basis: "working" });
  const reload = useCallback(async () => {
    if (!projectId) return;
    try { const { data } = await api.get("/schedule/relations", { params: { project_id: projectId } }); setRel(data); } catch { /* keep */ }
  }, [projectId]);
  useEffect(() => { reload(); }, [reload]);
  return { rel, reload };
};

const fmt = (d) => (d && /^\d{4}-\d{2}-\d{2}/.test(d) ? `${d.slice(5, 7)}/${d.slice(8, 10)}/${d.slice(2, 4)}` : d || "—");

/** Preview + per-lock selection for a parent whose descendants carry manual dates.
 *  Past locks are released automatically (a date that has passed cannot protect anything); current and
 *  future locks are ticked individually, so part of the schedule can move while the rest stays put. */
export function ScheduleConfirmDialog({ open, preview, selected, onToggle, onSelectAll, onClearAll, onApply, onCancel }) {
  if (!open || !preview) return null;
  const expired = preview.expired_locks || [];
  const picks = preview.selectable_locks || [];
  const nSel = picks.filter((l) => selected.has(l.token)).length;
  return (
    <Dialog open onOpenChange={(v) => { if (!v) onCancel(); }}>
      <DialogContent className="max-w-lg" data-testid="schedule-confirm-dialog">
        <DialogHeader>
          <DialogTitle className="text-base">Dates below this level are locked</DialogTitle>
          <DialogDescription className="text-xs">Tick the locked dates you want to release with this change.</DialogDescription>
        </DialogHeader>
        <div className="text-xs space-y-2 text-gray-800">
          <div><b>{preview.path}</b> · {preview.field} · {fmt(preview.old)} → <b>{fmt(preview.new)}</b></div>
          {preview.linked?.length > 0 && <div>Linked scopes moving too: <b>{preview.linked.join(", ")}</b></div>}
          <div>{preview.unlocked_descendants} unlocked room date{preview.unlocked_descendants === 1 ? "" : "s"} will follow</div>
          {expired.length > 0 && (
            <div className="rounded border border-emerald-200 bg-emerald-50 p-2" data-testid="schedule-expired-locks">
              <div className="font-semibold text-emerald-900">Past locked dates automatically unlocking: {expired.length}</div>
              <ul className="mt-1 max-h-28 overflow-y-auto space-y-0.5 text-emerald-900">
                {expired.map((l) => <li key={l.token} className="flex items-center gap-1"><Unlock className="w-3 h-3" />{l.path} · {fmt(l.value)}</li>)}
              </ul>
            </div>
          )}
          {picks.length > 0 && (
            <div className="rounded border bg-amber-50 p-2" data-testid="schedule-selectable-locks">
              <div className="flex items-center gap-2">
                <span className="font-semibold text-amber-900">Choose which locked dates to release</span>
                <button type="button" onClick={onSelectAll} className="ml-auto rounded border border-amber-300 bg-white px-1.5 text-[11px]" data-testid="schedule-select-all">Select all</button>
                <button type="button" onClick={onClearAll} className="rounded border border-amber-300 bg-white px-1.5 text-[11px]" data-testid="schedule-clear-all">Clear all</button>
              </div>
              <ul className="mt-1 max-h-48 overflow-y-auto space-y-0.5">
                {picks.map((l) => (
                  <li key={l.token}>
                    <label className="flex cursor-pointer items-center gap-1.5">
                      <input type="checkbox" checked={selected.has(l.token)} onChange={() => onToggle(l.token)}
                             data-testid={`schedule-lock-pick-${l.token}`} />
                      <Lock className="w-3 h-3 text-amber-700" />{l.path} · {fmt(l.value)}
                    </label>
                  </li>
                ))}
              </ul>
              <div className="mt-1 text-[11px] text-amber-800">{nSel} of {picks.length} will move · {picks.length - nSel} keep their date</div>
            </div>
          )}
          {preview.dependents?.length > 0 && (
            <div>Dependent dates: {preview.dependents.map((d) => `${d.path} ${fmt(d.old)}→${fmt(d.new)}`).join("; ")}</div>
          )}
          <div className="text-gray-500">{preview.total_changes} dates in total will change.</div>
        </div>
        <div className="flex flex-wrap justify-end gap-2 pt-2">
          <button type="button" onClick={onCancel} className="rounded border px-3 py-1 text-xs" data-testid="schedule-confirm-cancel">Cancel</button>
          <button type="button" onClick={onApply} className="rounded bg-teal-700 px-3 py-1 text-xs text-white" data-testid="schedule-confirm-apply">Apply selected changes</button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

/** The one save path both views use: dependency prompt → locked-children selection → apply. */
export function useScheduleSave({ projectId, actor, view, rel, onChanged }) {
  const [confirm, setConfirm] = useState(null);   // { preview, payload }
  const [selected, setSelected] = useState(new Set());
  const [depAsk, setDepAsk] = useState(null);     // { dep, payload }
  const post = async (payload) => {
    const { data } = await api.post("/frs-dates/level", payload);
    if (data.needs_confirm) {
      setConfirm({ preview: data.preview, payload });
      setSelected(new Set((data.preview.selectable_locks || []).map((l) => l.token)));
      return data;
    }
    const moved = (data.linked_updated || []).map((x) => x.to.scope_type).filter((v, i, a) => v && a.indexOf(v) === i);
    if (payload.value) {
      const bits = [];
      if (data.expired_unlocked) bits.push(`${data.expired_unlocked} past lock${data.expired_unlocked === 1 ? "" : "s"} released`);
      if (data.kept) bits.push(`${data.kept} kept`);
      if (moved.length) bits.push(`${moved.join(", ")} moved too`);
      toast.success(`${data.locked ? "Locked" : "Set"} — ${data.preview?.path || payload.level}${bits.length ? ` · ${bits.join(" · ")}` : ""}`);
    } else toast.success("Unlocked — re-inherits from above");
    await onChanged?.();
    return data;
  };
  // Re-price the preview every time the ticks change, so what the dialog says is what gets saved.
  const repreview = async (tokens) => {
    if (!confirm) return;
    try {
      const { data } = await api.post("/frs-dates/level", { ...confirm.payload, preview: true, unlock_tokens: [...tokens] });
      if (data.preview) setConfirm((c) => (c ? { ...c, preview: { ...data.preview } } : c));
    } catch { /* the ticks still apply; only the counts go stale */ }
  };
  const pick = (next) => { setSelected(next); repreview(next); };
  const save = async (node, field, value) => {
    const n = nodeOf(node);
    const payload = { project_id: projectId, level: n.level, field, value: value || "", scope_type: n.scope_type, building: n.building,
      floor: n.floor, area: n.area, room_number: n.room_number, room_type: n.room_type, by: actor || "", view };
    // RA note (2026-10): dependencies are retired from the UI — a typed date simply holds.
    try { return await post(payload); } catch (e) { toast.error(e.response?.data?.detail || "Could not save that date"); return null; }
  };
  const dialogs = (
    <>
      <ScheduleConfirmDialog open={!!confirm} preview={confirm?.preview} selected={selected}
        onToggle={(t) => { const next = new Set(selected); if (next.has(t)) next.delete(t); else next.add(t); pick(next); }}
        onSelectAll={() => pick(new Set((confirm?.preview?.selectable_locks || []).map((l) => l.token)))}
        onClearAll={() => pick(new Set())}
        onApply={async () => { const p = confirm.payload; const tokens = [...selected]; setConfirm(null);
          try { await post({ ...p, unlock_tokens: tokens }); } catch (e) { toast.error(e.response?.data?.detail || "Could not save"); } }}
        onCancel={() => { setConfirm(null); onChanged?.(); }} />
      {depAsk && (
        <Dialog open onOpenChange={(v) => { if (!v) { setDepAsk(null); onChanged?.(); } }}>
          <DialogContent className="max-w-md" data-testid="dependency-edit-dialog">
            <DialogHeader>
              <DialogTitle className="text-base">This date depends on {pathLabel(depAsk.dep.pred)} · {depAsk.dep.pred.scope_type}</DialogTitle>
              <DialogDescription className="text-xs">Currently +{depAsk.dep.offset_days} working days. What would you like to do with {fmt(depAsk.payload.value)}?</DialogDescription>
            </DialogHeader>
            <div className="flex flex-wrap justify-end gap-2">
              <button type="button" className="rounded border px-3 py-1 text-xs" onClick={() => { setDepAsk(null); onChanged?.(); }} data-testid="dep-edit-cancel">Cancel</button>
              <button type="button" className="rounded border border-red-300 px-3 py-1 text-xs text-red-700" data-testid="dep-edit-break"
                onClick={async () => { const { dep, payload } = depAsk; setDepAsk(null); try { await api.delete(`/schedule/deps/${dep.id}`, { params: { by: actor || "", view } }); await post({ ...payload, force_lock: true }); } catch (e) { toast.error("Could not break the dependency"); } }}>Break dependency, keep my date</button>
              <button type="button" className="rounded bg-teal-700 px-3 py-1 text-xs text-white" data-testid="dep-edit-offset"
                onClick={async () => { const { dep, payload } = depAsk; setDepAsk(null); try { await api.patch(`/schedule/deps/${dep.id}`, { succ_date: payload.value, by: actor || "", view }); toast.success("Offset updated — still depends on " + pathLabel(dep.pred)); await onChanged?.(); } catch (e) { toast.error(e.response?.data?.detail || "Could not update the offset"); } }}>Update offset</button>
            </div>
          </DialogContent>
        </Dialog>
      )}
    </>
  );
  return { save, dialogs };
}

const pill = (own) => `inline-flex items-center gap-0.5 rounded-full border px-1.5 text-[9px] font-semibold uppercase ${own ? "border-emerald-300 bg-emerald-50 text-emerald-800" : "border-dashed border-emerald-300 bg-white text-emerald-700"}`;

/** Chips + inline prompt beside one Start date: linked scopes, dependency, and the "link it?" question. */
export function ScopeLinkControl({ projectId, node: rawNode, scope, scopeLabel, others, items, rel, actor, view, prompt, onDismissPrompt, onChanged, alwaysShow = false, inline = false, canLink = true }) {
  // inline: the control sits inside the date popover, so its forms flow in place instead of floating
  const form = (w, tone) => (inline ? `mt-1 flex w-full max-w-[420px] flex-wrap items-center gap-1.5 rounded-md border ${tone} bg-white px-2 py-1.5 text-[11px] shadow-sm` : `absolute left-0 top-full z-40 mt-1 flex w-max ${w} flex-wrap items-center gap-1.5 rounded-md border ${tone} bg-white px-2 py-1.5 text-[11px] shadow-lg`);
  const n = nodeOf({ ...rawNode, scope_type: scope });
  const me = scopeLabel || scope;
  const codeFor = (t) => others.find((o) => o.type === t)?.code || t;
  const [mode, setMode] = useState("");   // "" | link | dep
  const [picked, setPicked] = useState({});
  const [pred, setPred] = useState("");
  const [offset, setOffset] = useState("5");
  const key = tok(n);
  const muteKey = `linkPromptOff:${projectId}:${key}`;
  useEffect(() => {
    if (!prompt) return;
    if (!canLink || localStorage.getItem(muteKey)) { onDismissPrompt?.(); return; }   // no link UI here, or user chose "don't ask again"
    setMode("link"); setPicked({});
  }, [prompt]); // eslint-disable-line react-hooks/exhaustive-deps
  const multi = !scope;   // a mixed-scope row: link any two or more scopes that live here
  const ga = multi ? null : groupAt(rel, n);
  const dep = null;   // dependencies retired from the UI (records kept)
  const hereGroups = multi ? (rel.groups || []).filter((g) => ctok(g.node) === ctok(n)) : [];
  const linkable = multi ? others : others.filter((o) => !(ga?.partners || []).includes(o.type) && !(ga?.suspended || []).includes(o.type));
  const deleteGroup = async (g) => {
    if (!window.confirm(`Unlink ${g.scopes.map(codeFor).join(", ")} here? No dates move.`)) return;
    try { await api.delete(`/schedule/groups/${g.id}`); await done("Unlinked — dates unchanged"); }
    catch { toast.error("Could not unlink"); }
  };
  const close = () => { setMode(""); setDateChoice(null); onDismissPrompt?.(); };
  const done = async (msg) => { if (msg) toast.success(msg); close(); await onChanged?.(); };

  // predecessor candidates: other nodes at this same level (any scope), from the FRS rows
  const candidates = () => {
    const seen = new Map();
    for (const i of items || []) {
      if (i.is_delta || i.is_punch || i.scope_type !== scope) continue;   // same scope only
      const c = nodeOf({ level: n.level, building: i.building, floor: i.floor, area: i.area,
        room_number: n.level === "room" ? i.room_number : "", room_type: n.level === "room" ? i.room_type : "", scope_type: i.scope_type });
      if (n.level === "building") c.floor = c.area = "";
      if (n.level === "floor") c.area = "";
      if (tok(c) !== tok(n)) seen.set(tok(c), c);
    }
    return [...seen.values()].sort((a, b) => `${pathLabel(a)}${a.scope_type}`.localeCompare(`${pathLabel(b)}${b.scope_type}`, undefined, { numeric: true }));
  };
  const [dateChoice, setDateChoice] = useState(null);   // { dates } when members start on different dates
  const link = async (useDate) => {
    const types = Object.keys(picked).filter((k) => picked[k]);
    if (types.length < (multi ? 2 : 1)) { toast.info(multi ? "Pick at least two scopes" : "Pick at least one scope"); return; }
    try {
      const body = { project_id: projectId, node: n, scopes: multi ? types : [scope, ...types], by: actor || "", view, use_date: useDate || "" };
      const { data } = await api.post("/schedule/groups", body);
      if (data.needs_date) { setDateChoice(data.preview); return; }
      setDateChoice(null);
      await done(multi ? `${types.map(codeFor).join(", ")} linked — one date, move any of them` : `${types.map(codeFor).join(", ")} linked with ${me} — one date, move any of them`);
    } catch (e) { toast.error(e.response?.data?.detail || "Could not link"); }
  };
  const unlink = async () => {
    const all = ga.group.scopes.map(codeFor).join(", ");
    if (ga.group.scopes.length > 2 && !window.confirm(`Take ${me} out of the linked group (${all})? The others stay linked. No dates move.`)) return;
    try { await api.post("/schedule/groups/remove", { project_id: projectId, node: n, scope, by: actor || "", view }); await done(`${me} unlinked${ga.own ? "" : ` at ${pathLabel(n)} and below`} — dates unchanged`); }
    catch (e) { toast.error(e.response?.data?.detail || "Could not unlink"); }
  };
  const addDep = async () => {
    const c = candidates().find((x) => tok(x) === pred);
    if (!c) { toast.info("Pick what this date should follow"); return; }
    try {
      const { data } = await api.post("/schedule/deps", { project_id: projectId, pred: c, succ: n, offset_days: Number(offset) || 0, by: actor || "", view });
      await done(`${me} now starts ${data.dep.offset_days} working days after ${pathLabel(c)} · ${codeFor(c.scope_type)} → ${fmt(data.preview.succ_new)}`);
    } catch (e) { toast.error(e.response?.data?.detail || "Could not add the dependency"); }
  };
  const breakDep = async () => {
    if (!window.confirm(`Break the dependency on ${pathLabel(dep.pred)} · ${codeFor(dep.pred.scope_type)}? The date stays where it is.`)) return;
    try { await api.delete(`/schedule/deps/${dep.id}`, { params: { by: actor || "", view } }); await done("Dependency broken — date kept"); }
    catch { toast.error("Could not break the dependency"); }
  };

  return (
    <span className="relative inline-flex flex-wrap items-center gap-1 align-middle" data-testid={`scope-links-${key}`}>
      {ga && (
        <span className={pill(ga.own)} title={`Linked: ${ga.group.scopes.map(codeFor).join(" = ")}${ga.own ? "" : ` (set at ${pathLabel(ga.at) || "whole scope"})`}. One date for all — move any of them and the rest follow.${ga.suspended.length ? ` ${ga.suspended.map(codeFor).join(", ")} is complete here, so the link is paused at this level until it is reopened.` : ""}${linkable.length ? " Click to link more scopes." : ""}`}
              data-testid={`scope-link-${key}`}>
          <button type="button" onClick={() => { if (canLink && linkable.length) { setMode("link"); setPicked({}); } }}
                  className={`inline-flex items-center gap-0.5 ${canLink && linkable.length ? "hover:underline cursor-pointer" : "cursor-default"}`}
                  data-testid={`scope-link-more-${key}`}>
            <Link2 className="w-2.5 h-2.5" />{ga.partners.map(codeFor).join(", ")}
            {ga.suspended.length > 0 && <span className="line-through opacity-50" data-testid={`scope-link-paused-${key}`}>{ga.partners.length ? ", " : ""}{ga.suspended.map(codeFor).join(", ")}</span>}
          </button>
          <button type="button" onClick={unlink} className="ml-0.5 text-teal-500 hover:text-red-600 leading-none" title={ga.own ? `Unlink ${me}` : `Unlink ${me} here and below`} data-testid={`scope-unlink-${key}`}>×</button>
        </span>
      )}
      {hereGroups.map((g) => (
        <span key={g.id} className={pill(true)} title={`Linked here: ${g.scopes.map(codeFor).join(" = ")} — one date for all`} data-testid={`scope-group-${key}-${g.id}`}>
          <button type="button" onClick={() => { if (canLink) { setMode("link"); setPicked(Object.fromEntries(g.scopes.map((x) => [x, true]))); } }}
                  className={`inline-flex items-center gap-0.5 ${canLink ? "hover:underline" : "cursor-default"}`} title={canLink ? "Click to add more scopes to this link" : "Linked scopes — one date for all"} data-testid={`scope-group-more-${key}-${g.id}`}>
            <Link2 className="w-2.5 h-2.5" />{g.scopes.map(codeFor).join(" = ")}
          </button>
          <button type="button" onClick={() => deleteGroup(g)} className="ml-0.5 text-teal-500 hover:text-red-600 leading-none" title="Unlink these scopes here" data-testid={`scope-group-unlink-${key}-${g.id}`}>×</button>
        </span>
      ))}
      {dep && (
        <span className="inline-flex items-center gap-0.5 rounded-full border border-indigo-300 bg-indigo-50 px-1.5 text-[9px] font-semibold text-indigo-800"
              title={`Depends on ${pathLabel(dep.pred)} · ${codeFor(dep.pred.scope_type)} + ${dep.offset_days} working days`} data-testid={`scope-dep-${key}`}>
          <ArrowRightToLine className="w-2.5 h-2.5" />{pathLabel(dep.pred)}{dep.pred.scope_type !== scope ? ` ${codeFor(dep.pred.scope_type)}` : ""} +{dep.offset_days}wd
          <button type="button" onClick={breakDep} className="ml-0.5 text-indigo-500 hover:text-red-600 leading-none" title="Break dependency" data-testid={`scope-dep-break-${key}`}>×</button>
        </span>
      )}
      {!mode && (
        <span className={`inline-flex gap-1 ${alwaysShow ? "" : "opacity-0 transition-opacity group-hover:opacity-100 focus-within:opacity-100"}`}>
          {canLink && linkable.length >= (multi ? 2 : 1) && !ga && !hereGroups.length && (
            <button type="button" onClick={() => { setMode("link"); setPicked({}); }} className="inline-flex items-center gap-0.5 rounded-full border border-gray-200 px-1.5 text-[9px] text-gray-400 hover:border-teal-300 hover:text-teal-700" title={multi ? "Link scopes at this level so they share one date" : `Link ${me} to another scope here`} data-testid={`scope-link-open-${key}`}><Link2 className="w-2.5 h-2.5" /> {multi ? "link scopes" : "link"}</button>
          )}

        </span>
      )}
      {mode === "link" && (
        <span className={`${form("max-w-[560px]", "border-teal-200")} text-teal-900`} data-testid={`scope-link-prompt-${key}`}>
          <span className="font-medium">{multi ? "Link scopes here:" : `Link ${me} here to`}</span>
          {linkable.map((o) => (
            <button key={o.type} type="button" onClick={() => setPicked((p) => ({ ...p, [o.type]: !p[o.type] }))}
                    className={`rounded-full border px-2 py-px text-[10px] font-semibold ${picked[o.type] ? "border-teal-600 bg-teal-600 text-white" : "border-gray-300 text-gray-700 hover:border-teal-400"}`}
                    data-testid={`scope-link-pick-${key}-${o.code}`}>{o.code}</button>
          ))}
          {linkable.length === 0 && <span className="text-gray-500">every scope here is already linked</span>}
          <span className="text-[10px] text-gray-500">— same date, two-way</span>
          {dateChoice ? (
            <span className="inline-flex flex-wrap items-center gap-1" data-testid={`scope-link-date-choice-${key}`}>
              <span className="text-[10px] font-medium text-amber-800">They start on different dates — use:</span>
              {[...new Set(Object.values(dateChoice.dates).filter(Boolean))].sort().map((d) => (
                <button key={d} type="button" onClick={() => link(d)} className="rounded border border-amber-300 bg-amber-50 px-1.5 py-px text-[10px] text-amber-900 hover:bg-amber-100"
                        data-testid={`scope-link-use-${key}-${d}`}>{fmt(d)} ({Object.entries(dateChoice.dates).filter(([, x]) => x === d).map(([s]) => codeFor(s)).join(", ")})</button>
              ))}
              {dateChoice.locked?.length > 0 && <span className="text-[10px] text-amber-700">· {dateChoice.locked.length} locked below</span>}
            </span>
          ) : (
            <button type="button" onClick={() => link("")} className="rounded bg-teal-700 px-2 py-px text-white" data-testid={`scope-link-save-${key}`}>Link</button>
          )}
          <button type="button" onClick={close} className="rounded border px-2 py-px text-gray-600" data-testid={`scope-link-no-${key}`}>No</button>
          {prompt && (
            <button type="button" onClick={() => { localStorage.setItem(muteKey, "1"); close(); toast.success("Won't ask again here — use the link button any time"); }}
                    className="rounded border px-2 py-px text-gray-500 hover:bg-gray-50" title="Stop asking when this date changes. The link button stays available."
                    data-testid={`scope-link-never-${key}`}>Don't ask again</button>
          )}
        </span>
      )}
      {mode === "dep" && (
        <span className={`${form("max-w-[620px]", "border-indigo-200")} text-indigo-900`} data-testid={`scope-dep-form-${key}`}>
          <span className="font-medium">{me} starts</span>
          <input type="number" value={offset} onChange={(e) => setOffset(e.target.value)} className="h-5 w-12 rounded border px-1 text-[11px]" data-testid={`scope-dep-offset-${key}`} />
          <span>working days after</span>
          <select value={pred} onChange={(e) => setPred(e.target.value)} className="h-5 max-w-[260px] rounded border text-[11px]" data-testid={`scope-dep-pred-${key}`}>
            <option value="">choose…</option>
            {candidates().map((c) => <option key={tok(c)} value={tok(c)}>{pathLabel(c)} · {codeFor(c.scope_type)}</option>)}
          </select>
          <button type="button" onClick={addDep} className="rounded bg-indigo-700 px-2 py-px text-white" data-testid={`scope-dep-save-${key}`}>Add</button>
          <button type="button" onClick={close} className="rounded border px-2 py-px text-gray-600">Cancel</button>
        </span>
      )}
    </span>
  );
}

// kept for callers that only need the lookups
export const effectiveLinks = groupAt;
export const useScopeLinks = useSchedule;
